/**
 * Buying AI credit (TMMAPP-0113 §C 7, TMMAPI-0151 as merged): the first purchase and a top-up, on
 * the card on file. Card data never touches the app: the request carries an amount and a fresh
 * `idempotencyKey` per attempt, never reused after an answer. A `PENDING` answer's one-time
 * `clientAction` runs once through `completeClientAction` (Stripe's `handleNextAction`), then the
 * wallet is re-read a few times until it is credited; after that the person gets a Refresh link.
 * Nothing about a charge is stored, logged or retried, and nothing AI runs afterwards on its own.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { aiWalletApi, type AiWallet } from '@/api/aiWallet';
import { ErrorCodes, isApiError } from '@/api/errors';
import { completeClientAction } from '@/features/billing/completeClientAction';
import { generateRequestId } from '@/utils/requestId';

/** The card-decline catalog's known codes (`card.declined.*`); anything else reads as a decline. */
const DECLINE_CODES: ReadonlySet<string> = new Set([
  'INSUFFICIENT_FUNDS',
  'CARD_EXPIRED',
  'CARD_DECLINED',
  'PSP_ERROR',
]);

export function declineKey(failureCode: string | null): string {
  return `card.declined.${failureCode !== null && DECLINE_CODES.has(failureCode) ? failureCode : 'CARD_DECLINED'}`;
}

/** The bounded wait for a pending charge: a few reads of the wallet, then a Refresh link. */
export const walletCreditPoll = { tries: 4, intervalMs: 1500 };

export type AiWalletChargeOutcome =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | {
      kind: 'added';
      amountMinor: number | null;
      currency: string | null;
      /** The served tax and card total (TMMAPI-0156 U2); null when not served. */
      taxMinor: number | null;
      totalMinor: number | null;
    }
  | { kind: 'processing' }
  | { kind: 'processingSlow' }
  | { kind: 'declined'; failureCode: string | null }
  | { kind: 'actionFailed' }
  | { kind: 'noCard' }
  | { kind: 'unavailable' }
  | { kind: 'subscriptionRequired' }
  | { kind: 'conflict' }
  /**
   * `422 TAX_LOCATION_REQUIRED` (TMMAPI-0156 U2): nothing was charged. Once the person updates the
   * card with its full billing address, `retry` runs the same charge once, with a fresh key.
   */
  | { kind: 'taxLocationRequired'; retry: () => void }
  /** `409 WALLET_DISPUTE_HOLD` (TMMAPI-0159): a dispute on an earlier wallet charge is open. */
  | { kind: 'disputeHold' }
  /** `503 BILLING_CONFIGURATION` (TMMAPI-0159): not retryable; the Reference is shown. */
  | { kind: 'billingConfiguration'; requestId: string | null }
  | { kind: 'error' };

/** The answers the dialog keeps showing; any other closes it for the host to show. */
export function topUpStaysOpen(outcome: AiWalletChargeOutcome): boolean {
  return (
    outcome.kind !== 'added' && outcome.kind !== 'processing' && outcome.kind !== 'processingSlow'
  );
}

/**
 * A charge is still in flight: the request, or a pending charge settling (QA, TMMAPP-0113). ENABLE AI
 * and TOP UP stay off until it is credited (or the page reloads), so a second press can't put a
 * second charge on the card while the first is pending at the PSP; Refresh is the only action.
 */
export function chargeInFlight(outcome: AiWalletChargeOutcome): boolean {
  return (
    outcome.kind === 'busy' || outcome.kind === 'processing' || outcome.kind === 'processingSlow'
  );
}

function credited(before: AiWallet | null, after: AiWallet): boolean {
  if (before === null) return after.balanceMinor > 0;
  if (!before.firstPurchaseDone && after.firstPurchaseDone) return true;
  return after.balanceMinor > before.balanceMinor;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const id = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(id);
      resolve();
    });
  });
}

interface Options {
  /** The wallet as the person sees it now: a pending charge is credited once the balance rises. */
  wallet: AiWallet | null;
  /** Each fresh read of the wallet (after a success, and while a pending charge settles). */
  onWallet: (wallet: AiWallet | null) => void;
}

export function useAiWalletCharge({ wallet, onWallet }: Options) {
  const [outcome, setOutcome] = useState<AiWalletChargeOutcome>({ kind: 'idle' });
  const abort = useRef<AbortController | null>(null);
  const walletRef = useRef(wallet);
  const onWalletRef = useRef(onWallet);
  // The charge a tax-location retry reruns (item 11); set to `run` below, after each render.
  const runRef = useRef<(kind: 'purchase' | 'topUp', amountMinor?: number) => Promise<void>>(
    async () => undefined,
  );
  useEffect(() => {
    walletRef.current = wallet;
    onWalletRef.current = onWallet;
  });
  useEffect(() => () => abort.current?.abort(), []);

  const settle = useCallback(async (before: AiWallet | null, signal: AbortSignal) => {
    for (let attempt = 0; attempt < walletCreditPoll.tries; attempt += 1) {
      if (attempt > 0) await sleep(walletCreditPoll.intervalMs, signal);
      if (signal.aborted) return null;
      try {
        const next = await aiWalletApi.get();
        if (signal.aborted) return null;
        onWalletRef.current(next);
        if (credited(before, next)) return next;
      } catch {
        /* the next read, or the Refresh link */
      }
    }
    return null;
  }, []);

  const run = useCallback(
    async (kind: 'purchase' | 'topUp', amountMinor?: number) => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      const before = walletRef.current;
      setOutcome({ kind: 'busy' });
      // A fresh key per attempt: an answer is final for its key, and the next press is a new attempt.
      const idempotencyKey = generateRequestId();
      try {
        const charge =
          kind === 'purchase'
            ? await aiWalletApi.purchase(idempotencyKey, amountMinor)
            : await aiWalletApi.topUp(amountMinor ?? 0, idempotencyKey);
        if (controller.signal.aborted) return;
        if (charge.status === 'SUCCEEDED') {
          onWalletRef.current(charge.wallet);
          setOutcome({
            kind: 'added',
            amountMinor: charge.amountMinor,
            currency: charge.currency,
            taxMinor: charge.taxMinor,
            totalMinor: charge.totalMinor,
          });
          return;
        }
        if (charge.status === 'FAILED') {
          setOutcome({ kind: 'declined', failureCode: charge.failureCode });
          return;
        }
        if (charge.clientAction !== null) {
          const result = await completeClientAction(charge.clientAction);
          if (controller.signal.aborted) return;
          if (result.kind === 'failed') {
            setOutcome({ kind: 'actionFailed' });
            return;
          }
        }
        setOutcome({ kind: 'processing' });
        const next = await settle(before, controller.signal);
        if (controller.signal.aborted) return;
        if (next === null) {
          setOutcome({ kind: 'processingSlow' });
          return;
        }
        setOutcome({
          kind: 'added',
          amountMinor: charge.amountMinor,
          currency: charge.currency,
          taxMinor: charge.taxMinor,
          totalMinor: charge.totalMinor,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        if (isApiError(error) && error.code === ErrorCodes.TAX_LOCATION_REQUIRED) {
          // The retry is a new attempt: `run` mints a new Idempotency-Key for it. It runs at most
          // once per refusal (TMMAPP-0122 item 25): a second card update can't charge twice.
          let retried = false;
          setOutcome({
            kind: 'taxLocationRequired',
            retry: () => {
              if (retried) return;
              retried = true;
              void runRef.current(kind, amountMinor);
            },
          });
          return;
        }
        setOutcome(failureOutcome(error));
      }
    },
    [settle],
  );
  useEffect(() => {
    runRef.current = run;
  }, [run]);

  /** A manual re-read after a slow pending charge (the Refresh link). */
  const refresh = useCallback(async () => {
    const before = walletRef.current;
    try {
      const next = await aiWalletApi.get();
      onWalletRef.current(next);
      if (credited(before, next)) {
        setOutcome({
          kind: 'added',
          amountMinor: null,
          currency: next.currency,
          taxMinor: null,
          totalMinor: null,
        });
      }
    } catch {
      /* stays as it is */
    }
  }, []);

  const reset = useCallback(() => {
    abort.current?.abort();
    setOutcome({ kind: 'idle' });
  }, []);

  return {
    outcome,
    purchase: (amountMinor?: number) => run('purchase', amountMinor),
    topUp: (amountMinor: number) => run('topUp', amountMinor),
    refresh,
    reset,
  };
}

function failureOutcome(error: unknown): AiWalletChargeOutcome {
  if (!isApiError(error)) return { kind: 'error' };
  const backend = error.details.backend;
  const bag = typeof backend === 'object' && backend !== null ? backend : {};
  switch (error.code) {
    case ErrorCodes.WALLET_CHARGE_FAILED:
      return {
        kind: 'declined',
        failureCode: typeof bag.failureCode === 'string' ? bag.failureCode : null,
      };
    case ErrorCodes.WALLET_CHARGE_UNAVAILABLE:
      // `reason: NO_CARD` (a comp, or no pay-api id); pay-api's own "no card on file" reads the same.
      return bag.reason === 'NO_CARD' || bag.payApiCode === 'SUBSCRIPTION_NO_CARD_ON_FILE'
        ? { kind: 'noCard' }
        : { kind: 'unavailable' };
    case ErrorCodes.SUBSCRIPTION_REQUIRED:
      return { kind: 'subscriptionRequired' };
    case ErrorCodes.IDEMPOTENCY_CONFLICT:
      return { kind: 'conflict' };
    case ErrorCodes.WALLET_DISPUTE_HOLD:
      return { kind: 'disputeHold' };
    case ErrorCodes.BILLING_CONFIGURATION:
      return { kind: 'billingConfiguration', requestId: error.requestId ?? null };
    default:
      return { kind: 'error' };
  }
}
